import { Box, Text, Link, VStack, ButtonGroup, Button, useTheme } from '@chakra-ui/react';
import { useCustomThemeDraftDispatch, useCustomThemeDraftState } from '@olo-web/client-state';
import { useMemo } from 'react';
import isObject from 'lodash/isObject';
import { useUpdateMerchantTheme } from '@domain/merchants/mutations/useUpdateMerchantTheme';
import { useError } from '@olo-web/utils/common/hooks';
import { EditableInputWithButtons } from '@olo-web/components/molecules/EditableInputWithButtons';

export const ThemeFonts = () => {
  const callError = useError();
  const { fonts } = useTheme();
  const { fonts: draftFonts } = useCustomThemeDraftState();
  const themeDispatch = useCustomThemeDraftDispatch();

  const handleRestore = () => {
    themeDispatch({ type: 'CLEAR_FONTS' });
  };

  const { mutate, isLoading } = useUpdateMerchantTheme({
    onSuccess: handleRestore,
    onError: (error) => callError({ error, toastError: true, title: 'Unable to save fonts' }),
  });

  const draftFontsExist = useMemo(() => {
    return isObject(draftFonts) && !!Object.keys(draftFonts).length;
  }, [draftFonts]);

  const handleSave = () => {
    mutate({
      theme: { fonts },
    });
  };

  const handleSubmit = (label: string, value: string) => {
    themeDispatch({
      type: 'UPDATE_FONTS',
      payload: { [label]: value },
    });
  };

  return (
    <Box w="100%">
      <Text as="h4" textStyle="h4">
        Fonts
      </Text>
      <Text textStyle="label" mb={6}>
        All fonts should be in string{' '}
        <Link
          isExternal
          textDecor="underline"
          color="primary.350"
          href="https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Fonts"
          aria-label="CSS format (opens in new window)"
        >
          CSS format
        </Link>
        , (ie, &quot;Circular, Roboto, sans-serif&quot;)
      </Text>
      <VStack align="flex-end">
        {Object.keys(fonts).map((key, idx) =>
          key === 'mono' ? null : (
            <EditableInputWithButtons
              key={`${key}-${idx}-${fonts[key]}`}
              label={key}
              defaultValue={fonts[key]}
              onSubmit={(value: string) => handleSubmit(key, value)}
            />
          )
        )}
        {draftFontsExist && (
          <ButtonGroup pt={4}>
            <Button
              colorScheme="primary"
              variant="solid"
              onClick={handleSave}
              isLoading={isLoading}
            >
              Save fonts
            </Button>
            <Button colorScheme="danger" variant="tertiary" onClick={handleRestore}>
              Restore
            </Button>
          </ButtonGroup>
        )}
      </VStack>
    </Box>
  );
};
